import { useEffect, useState } from "react";
import axios from "../api/axios";
import { Alert, Box } from "@mui/material";
import { getActiveRole } from "../utils/auth";

function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const role = getActiveRole();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (role !== "super_admin") {
      setLoading(false);
      return;
    }

    axios
      .get("/profile/admin/verification-logs", { headers })
      .then((res) => {
        if (res.data.success) {
          setLogs(res.data.logs || []);
        } else {
          setError("Failed to fetch logs");
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Error fetching audit logs");
      })
      .finally(() => setLoading(false));
  }, [role]);

  if (role !== "super_admin") {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Access Denied: Only Super Admins can access global verification logs.</Alert>
      </Box>
    );
  }

  const filteredLogs = logs.filter((log) => {
    const searchString = `${log.operator_email} ${log.target_email || ""} ${log.target_profile_id || ""} ${log.table_name} ${log.action_type}`.toLowerCase();
    return searchString.includes(searchTerm.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900/10 p-4 md:p-6 transition-all duration-300">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Block */}
        <div className="bg-white dark:bg-slate-800/40 rounded-2xl shadow-sm border border-slate-200/60 dark:border-slate-700 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2.5 font-sans">
              <span>📋</span> Global Verification Logs
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Super Admin Audit Desk: Complete historical log of profile verifications, unverifications, and rejections.
            </p>
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="Search audit events..."
              className="w-full md:w-80 px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900/60 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-sans"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <Alert severity="error" className="rounded-xl">
            {error}
          </Alert>
        )}

        {/* Content Block */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-sm text-slate-500 mt-4 italic font-sans">Loading verification logs...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="bg-white dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-700 rounded-2xl p-12 text-center shadow-sm">
            <span className="text-4xl">📭</span>
            <p className="text-slate-500 dark:text-slate-400 mt-3 font-sans font-medium">
              No audit logs found matching your query.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm text-slate-700 dark:text-slate-300">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-800 dark:text-slate-200 font-semibold border-b border-slate-200/60 dark:border-slate-700 font-sans">
                  <tr>
                    <th className="px-6 py-4">Timestamp</th>
                    <th className="px-6 py-4">Operator Email</th>
                    <th className="px-6 py-4">Action</th>
                    <th className="px-6 py-4">Target Faculty</th>
                    <th className="px-6 py-4">Profile ID</th>
                    <th className="px-6 py-4">Table & Record ID</th>
                    <th className="px-6 py-4">Rejection Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                  {filteredLogs.map((log) => (
                    <tr
                      key={log.id}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-all duration-150"
                    >
                      <td className="px-6 py-4 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap font-medium">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-800 dark:text-slate-100 whitespace-nowrap">
                        {log.operator_email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide ${
                            log.action_type === "verify"
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/35 dark:text-emerald-300"
                              : log.action_type === "reject"
                              ? "bg-rose-100 text-rose-800 dark:bg-rose-900/35 dark:text-rose-300"
                              : "bg-amber-100 text-amber-800 dark:bg-amber-900/35 dark:text-amber-300"
                          }`}
                        >
                          {log.action_type === "verify"
                            ? "Verified"
                            : log.action_type === "reject"
                            ? "Rejected"
                            : "Unverified"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-slate-800 dark:text-slate-100 font-medium">
                        {log.target_email || <span className="text-slate-400 dark:text-slate-600 italic">N/A</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {log.target_profile_id ? (
                          <code className="bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded text-xs font-mono font-semibold text-slate-700 dark:text-slate-300">
                            {log.target_profile_id}
                          </code>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-600 italic text-xs">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs font-mono text-slate-600 dark:text-slate-400 whitespace-nowrap">
                        <code className="bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded font-semibold text-blue-600 dark:text-blue-400">
                          {log.table_name}
                        </code>{" "}
                        <span className="text-slate-400 dark:text-slate-600 font-semibold">(ID: {log.record_id})</span>
                      </td>
                      <td className="px-6 py-4 text-xs text-rose-600 dark:text-rose-400 italic max-w-xs truncate">
                        {log.rejection_reason || (
                          <span className="text-slate-400 dark:text-slate-600 not-italic">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Logs;
